<template>
  <div :class="$style.wrapper">
    <div
      ref="container"
      :class="$style.container"
      @scroll="updateGeometry"
    >
      <div
        ref="items"
        :class="[$style.items, itemsClass]"
      >
        <slot />
      </div>
    </div>
    <skeleton
      :zone="zone"
      :relative="false"
      @content:appeared="updateGeometry"
    >
      <div
        v-if="isLeftArrowVisible"
        :class="[$style.buttonWrapper, buttonWrapperClass, $style.buttonWrapperLeft]"
      >
        <button
          :class="$style.button"
          @click="handleScrollLeft"
        >
          <base-icon type="chevron-left-20" />
        </button>
      </div>
      <div
        v-if="isRightArrowVisible"
        :class="[$style.buttonWrapper, buttonWrapperClass, $style.buttonWrapperRight]"
      >
        <button
          :class="$style.button"
          @click="handleScrollRight"
        >
          <base-icon type="chevron-right-20" />
        </button>
      </div>
    </skeleton>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import BaseIcon from '@/shared/legacy/ui/base-icon/icon.vue';
import { Skeleton } from '@/shared/legacy/ui/skeleton';
import { AnimateHelper } from '@/shared/legacy/lib/util/animate-helper';

const MAGNIFICATION_DISTANCE = 3;
const ANIMATION_DURATION = 200;
const ACTIVE_ITEM_LEFT_OFFSET = 100;
const SCROLL_VIEWPORT_SAFE_ZONE = 50;

export default defineComponent({
  name: 'BaseCarousel',
  components: {
    BaseIcon,
    Skeleton
  },
  props: {
    zone: {
      type: String,
      required: true
    },
    buttonWrapperClass: {
      type: String,
      default: null
    },
    itemsClass: {
      type: String,
      default: null
    },
    activeItemIndex: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      itemsNodeWidth: 0,
      viewportNodeWidth: 0,
      leftPos: 0
    };
  },
  computed: {
    isLeftArrowVisible() {
      return this.leftPos > MAGNIFICATION_DISTANCE;
    },
    isRightArrowVisible() {
      return this.leftPos + this.viewportNodeWidth < this.itemsNodeWidth - MAGNIFICATION_DISTANCE;
    }
  },
  watch: {
    activeItemIndex(newIndex, oldIndex) {
      if (newIndex == null) {
        return;
      }

      if (newIndex !== oldIndex) {
        this.ensureActiveItemVisible();
      }
    }
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(this.updateGeometry);
    this.resizeObserver.observe(this.$refs.items);
    this.resizeObserver.observe(this.$refs.container);
    this.mutationObserver = new MutationObserver(this.updateGeometry);
    this.mutationObserver.observe(this.$refs.items, {
      childList: true,
      subtree: true
    });
    this.applyInitialScroll();
  },
  beforeUnmount() {
    this.stopAnimation?.();
    this.mutationObserver.disconnect();
    this.resizeObserver.disconnect();
  },
  methods: {
    updateGeometry() {
      const { container, items } = this.$refs;
      if (!container || !items) {
        return;
      }
      this.leftPos = container.scrollLeft;
      this.itemsNodeWidth = items.clientWidth;
      this.viewportNodeWidth = container.clientWidth;
    },
    doScroll(leftTo) {
      this.stopAnimation?.();
      if (!this.$refs.container) {
        return;
      }
      this.stopAnimation = AnimateHelper.scrollTo(
        this.$refs.container,
        { left: leftTo },
        {
          duration: ANIMATION_DURATION,
          easing: AnimateHelper.swingEasing,
          end: () => {
            this.updateGeometry();
          }
        }
      );
    },
    handleScrollLeft() {
      let newLeftPos = this.leftPos - Number.parseInt(this.viewportNodeWidth / 2);
      if (newLeftPos < MAGNIFICATION_DISTANCE) {
        newLeftPos = 0;
      }
      this.doScroll(newLeftPos);
    },
    handleScrollRight() {
      let newLeftPos = this.leftPos + Math.ceil(this.viewportNodeWidth / 2);
      if (newLeftPos + this.viewportNodeWidth > this.itemsNodeWidth - MAGNIFICATION_DISTANCE) {
        newLeftPos = this.itemsNodeWidth - this.viewportNodeWidth;
      }
      this.doScroll(newLeftPos);
    },
    clampScrollLeft(value) {
      const maxScrollLeft = this.$refs.container.scrollWidth - this.$refs.container.clientWidth;
      return Math.max(0, Math.min(value, maxScrollLeft));
    },
    getElementScrollLeft(element, offset) {
      const containerRect = this.$refs.container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      return this.$refs.container.scrollLeft + elementRect.left - containerRect.left + offset;
    },
    getActiveChild() {
      if (this.activeItemIndex == null) {
        return null;
      }
      return this.$refs.items?.children.item(this.activeItemIndex) ?? null;
    },
    applyInitialScroll() {
      const child = this.getActiveChild();
      const container = this.$refs.container;
      if (!container || !child) {
        return;
      }

      requestAnimationFrame(() => {
        // The component can already be unmounted by the time the frame runs,
        // and then the container ref is null.
        if (!this.$refs.container) {
          return;
        }
        const left = this.clampScrollLeft(
          this.getElementScrollLeft(child, -ACTIVE_ITEM_LEFT_OFFSET)
        );
        container.scrollTo({ left });
        this.updateGeometry();
      });
    },
    ensureActiveItemVisible() {
      const child = this.getActiveChild();
      const container = this.$refs.container;
      if (!container || !child) {
        return;
      }

      requestAnimationFrame(() => {
        if (!this.$refs.container) {
          return;
        }
        const containerRect = container.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();
        const currentScrollLeft = container.scrollLeft;
        const visibleLeft = containerRect.left + SCROLL_VIEWPORT_SAFE_ZONE;
        const visibleRight = containerRect.right - SCROLL_VIEWPORT_SAFE_ZONE;
        let nextLeft = currentScrollLeft;

        if (childRect.left < visibleLeft) {
          nextLeft = this.clampScrollLeft(
            this.getElementScrollLeft(child, -ACTIVE_ITEM_LEFT_OFFSET)
          );
        } else if (childRect.right > visibleRight) {
          nextLeft = this.clampScrollLeft(
            currentScrollLeft + childRect.right - containerRect.right + ACTIVE_ITEM_LEFT_OFFSET
          );
        }

        if (nextLeft !== currentScrollLeft) {
          this.doScroll(nextLeft);
        }
      });
    }
  }
});
</script>

<style module>
.wrapper {
  position: relative;
  height: 100%;
}
.container {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: auto;

  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.items,
.items > * {
  display: flex;
  height: 100%;
  white-space: nowrap;
  flex-wrap: nowrap;
}
.buttonWrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
}
.buttonWrapperLeft {
  left: 0;
  justify-content: flex-start;
  background: linear-gradient(90deg, $white 80%, rgba(255, 255, 255, 0) 100%);
}
.buttonWrapperRight {
  right: 0;
  justify-content: flex-end;
  background: linear-gradient(270deg, $white 80%, rgba(255, 255, 255, 0) 100%);
}
.button {
  @mixin reset-button-styles;
  display: inline-flex;
  cursor: pointer;
  padding: 4px;
  margin: 0;
  background-color: $white;
  border-radius: 50%;
  box-shadow: 0px 4px 24px 0px #05050514;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }

  transition: transform 100ms ease-out;
  will-change: transform;
  backface-visibility: hidden;
  &:hover {
    transform: scale(1.2857);
  }
}
</style>

<i18n lang="json">{}</i18n>
